import type { StarGridTextKey } from 'src/components/star-grid/hooks/useStarGridText';
import type { AccessCheckResponse, ColumnRole, DatasetFixedFilter, DatasetProfile } from 'src/components/star-grid/datasets/datasetTypes';
import { COVERAGE_BLOCK } from 'src/components/star-grid/datasets/datasetConstants';
import { choiceBlocks, effectiveHierarchyChoice, type HierarchyChoice } from 'src/components/star-grid/datasets/utils/datasetHierarchy';
import { valueCount } from 'src/components/star-grid/datasets/utils/fixedFilterOps';

export type WizardStepId = 'source' | 'access' | 'map' | 'scenarios' | 'review';

export const WIZARD_STEPS: readonly WizardStepId[] = ['source', 'access', 'map', 'scenarios', 'review'];

/** Editing a saved dataset: its source, fixed filters and access are fixed, so the wizard opens at Map Columns. */
export const EDIT_WIZARD_STEPS: readonly WizardStepId[] = ['map', 'scenarios', 'review'];

export function wizardSteps(editing: boolean): readonly WizardStepId[] {
  return editing ? EDIT_WIZARD_STEPS : WIZARD_STEPS;
}

export const WIZARD_STEP_TITLE_KEYS: Record<WizardStepId, StarGridTextKey> = {
  source: 'wizardStepSource',
  access: 'wizardStepAccess',
  map: 'wizardStepMap',
  scenarios: 'wizardStepScenarios',
  review: 'wizardStepReview'
};

/** The snapshot of wizard state the step gates read. */
export interface WizardGateState {
  readonly selectedRef: string | null;
  readonly access: AccessCheckResponse | null;
  readonly profile: DatasetProfile | null;
  readonly roles: Readonly<Record<string, ColumnRole>>;
  readonly hierarchyChoices: Readonly<Record<string, HierarchyChoice>>;
  readonly name: string;
  readonly fixedFilters?: readonly DatasetFixedFilter[];
}

/** A fixed filter is saved only once it names a column and every value its operator needs. */
export function fixedFilterComplete(filter: DatasetFixedFilter): boolean {
  const values = filter.values.filter((value) => value.trim() !== '');
  if (!filter.column) return false;
  const count = valueCount(filter.op);
  if (count === 'many') return values.length > 0;
  return values.length === count && filter.values.length === count;
}

function effectiveRole(name: string, suggested: ColumnRole, roles: Readonly<Record<string, ColumnRole>>): ColumnRole {
  return roles[name] ?? suggested;
}

function hasMeasure(state: WizardGateState): boolean {
  return Boolean(state.profile?.columns.some((column) => effectiveRole(column.name, column.suggested_role, state.roles) === 'measure'));
}

/** True when a dimension keeps a hierarchy whose known coverage is below the blocking threshold. */
export function hierarchyBlocked(state: WizardGateState): boolean {
  return Boolean(
    state.profile?.columns.some(
      (column) =>
        effectiveRole(column.name, column.suggested_role, state.roles) === 'dimension' &&
        choiceBlocks(effectiveHierarchyChoice(column, state.hierarchyChoices), COVERAGE_BLOCK)
    )
  );
}

/** Whether the step's forward action is allowed. The review step gates Save on a name. */
export function canAdvance(step: WizardStepId, state: WizardGateState): boolean {
  switch (step) {
    case 'source':
      return Boolean(state.selectedRef) && (state.fixedFilters ?? []).every(fixedFilterComplete);
    case 'access':
      return state.access?.accessible === true;
    case 'map':
      return Boolean(state.profile) && hasMeasure(state) && !hierarchyBlocked(state);
    // An unmapped scenario is allowed: a report reading it names it.
    case 'scenarios':
      return true;
    case 'review':
      return state.name.trim().length > 0 && !hierarchyBlocked(state);
    default:
      return false;
  }
}

export function stepIndex(step: WizardStepId, steps: readonly WizardStepId[] = WIZARD_STEPS): number {
  return steps.indexOf(step);
}

export function isLastStep(step: WizardStepId, steps: readonly WizardStepId[] = WIZARD_STEPS): boolean {
  return stepIndex(step, steps) === steps.length - 1;
}
